import { PaginatedDataDTO } from "@bottega52/commons-pagination";
import { Button, Card } from "@material-ui/core";
import { WithStyles, createStyles, withStyles } from '@material-ui/core/styles';
import DomainsIcon from '@material-ui/icons/Domain';
import WarningIcon from '@material-ui/icons/Warning';
import * as React from "react";
import { ConnectedProps, connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as CreditsActions from '../../redux/credits/credits.actions';
import * as ModalsActions from '../../redux/modals/modals.actions';
import * as SettingsActions from '../../redux/settings/settings.actions';
import { IState } from '../../redux/store';
import { ICustomerContractInDTO } from "../../repository/jago/model/input/ICustomerContractInDTO";
import { ICustomerInDTO } from "../../repository/jago/model/input/ICustomerInDTO";
import { IDomainInDTO } from "../../repository/jago/model/input/IDomainInDTO";
import AbilityProvider from "../../services/PermissionService/AbilityProvider";
import { PERMISSIONS } from "../../services/PermissionService/PermissionConstants";
import { IOption } from "../Forms/VarOpportunityForm";
import { ModalTypes } from "../Modals/ModalTypes";
import ReduxLanguage from "../ReduxLanguage";
import CustomerContractDomainsTable from "./CustomerContractDomainsTable";

const styles = createStyles({
  container: {
    display: 'flex',
    marginTop: 20,
    flexDirection: 'column',
  },
  cardContainer: {
    padding: 10,
    borderLeft: '3px solid #5AC0B1',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerInner: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTextContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  icon: {
    marginRight: 5,
    width: 25,
  },
  title: {
    fontWeight: 'bold'
  },
  tableContainer: {
    display: 'flex',
    backgroundColor: 'white',
    height: 'calc(100vh - 380px)',
    width: '100%',
    marginTop: 10
  }
});

export interface ICustomerContractPlantsSectionState {
  pageSize: number;
}

type IReduxProps = ConnectedProps<typeof connector> & RouteComponentProps<any>;

export interface ICustomerContractPlantsSectionProps extends WithStyles<typeof styles> {
  language: string;
  customer: ICustomerInDTO;
}

export type ComponentProps = ICustomerContractPlantsSectionProps & IReduxProps;

class CustomerContractPlantsSection extends React.Component<ComponentProps, ICustomerContractPlantsSectionState> {

  constructor(props: ComponentProps) {
    super(props);
    this.state  = {
      pageSize: 1000
    };
  }

  addPlantRequest() {
    const { dispatch, customerPlants, selectedCustomerContract } = this.props;
    const customerPlantsData = (customerPlants as PaginatedDataDTO<IDomainInDTO>).content
    const customerPlantsDataFiltered = customerPlantsData.filter(plant => !((selectedCustomerContract as ICustomerContractInDTO).plants || []).some(p => p.id === plant.id))
    const options: IOption[] = customerPlantsDataFiltered.map(plant => ({id: plant.id, value: plant.hostname}))
    dispatch<any>(ModalsActions.showModal(`ADD_CUSTOMER_PLANT_CONFIRM_MODAL`, {
      modalType: ModalTypes.SELECT_MODAL,
      modalProps: {
        initialID: null,
        options: options,
        fieldLabel: null,
        placeHolder: null,
        successMessageKey: 'credits.addCustomerPlantMessage',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: (value: IOption|{}) => {if (!(value as IOption) || !(value as IOption).id) return; this.addPlantConfirm((value as IOption).id)},
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`ADD_CUSTOMER_PLANT_CONFIRM_MODAL`)),
      }
    }));
  }

  async addPlantConfirm(customerPlantId: number) {
    const { dispatch, selectedCustomerContract } = this.props;
    try {
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      const customerContractData = selectedCustomerContract as ICustomerContractInDTO;
      await dispatch<any>(CreditsActions.addCustomerPlantToContract(customerContractData.customerId, customerContractData.id, customerPlantId));
      const updatedContracts = await dispatch<any>(CreditsActions.fetchCustomerContracts({ page: 0, pageSize: 1000, customerId: customerContractData.customerId }));
      if ((window?.location?.pathname || "").startsWith('/contracts')) {
        await dispatch<any>(CreditsActions.fetchAllCustomerContracts({ page: 0, pageSize: 50 }))
      }
      const updatedContractsData = (updatedContracts as PaginatedDataDTO<ICustomerContractInDTO>).content;
      const updatedContract = updatedContractsData.find(contract => contract.id === customerContractData.id);
      if (updatedContract) {
        dispatch<any>(CreditsActions.setSelectedCustomerContract(updatedContract));
      }
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      await dispatch<any>(ModalsActions.hideModal('ADD_CUSTOMER_PLANT_CONFIRM_MODAL'));
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_OPPORTUNITYOWNER_EDIT`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'credits.addCustomerPlantToContractError',
        }
      }));
    }
  }

  onRemovePlantFromContractRequest(customerPlant: IDomainInDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`REMOVE_CUSTOMER_PLANT_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'forms.warning',
        successMessageKey: 'credits.removeCustomerPlantFromContractConfirm',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onDeleteCustomerPlantConfirm(customerPlant),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`REMOVE_CUSTOMER_PLANT_CONFIRM_MODAL`)),
      }
    }));
  }

  async onDeleteCustomerPlantConfirm(customerPlant: IDomainInDTO) {
    const { dispatch, selectedCustomerContract } = this.props;
    try {
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      const customerContractData = selectedCustomerContract as ICustomerContractInDTO;
      await dispatch<any>(CreditsActions.removeCustomerPlantFromContract(customerContractData.customerId, customerContractData.id, customerPlant.id));
      const updatedContracts = await dispatch<any>(CreditsActions.fetchCustomerContracts({ page: 0, pageSize: 1000, customerId: customerContractData.customerId }));
      if ((window?.location?.pathname || "").startsWith('/contracts')) {
        await dispatch<any>(CreditsActions.fetchAllCustomerContracts({ page: 0, pageSize: 50 }))
      }
      const updatedContractsData = (updatedContracts as PaginatedDataDTO<ICustomerContractInDTO>).content;
      const updatedContract = updatedContractsData.find(contract => contract.id === customerContractData.id);
      if (updatedContract) {
        dispatch<any>(CreditsActions.setSelectedCustomerContract(updatedContract));
      }
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.hideModal('REMOVE_CUSTOMER_PLANT_CONFIRM_MODAL'));
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_CUSTOMER_PLANT_DELETE`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'credits.removeCustomerPlantFromContractError',
        }
      }));
    }
  }
  
  public render() {
    const { selectedCustomerContract, classes } = this.props;
    const { pageSize } = this.state;
    
    const canAssociateNewPlant = AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CONTRACTS_UPDATE)

    const customerContractPlants = (selectedCustomerContract as ICustomerContractInDTO).plants || [];
    const customerContractPlantsPaginated = {
      content: customerContractPlants,
      pagination: {
        page: 0,
        pageSize: customerContractPlants.length,
        totalPages: 1
      }
    } as PaginatedDataDTO<IDomainInDTO>;

    return (
      <div className={classes.container}>
        <Card className={classes.cardContainer}>
          <div className={classes.header}>
            <div className={classes.headerInner}>
              <DomainsIcon className={classes.icon} />
              <h3 className={classes.title}>{<ReduxLanguage languageKey="credits.walletPlants"/>}</h3>
              <h5 className={classes.title} style={{ marginLeft: 0 }}>{` (${customerContractPlants.length})`}</h5>
            </div>
            {canAssociateNewPlant && 
              <Button
                variant="contained"
                style={{ fontWeight: 'bold', backgroundColor: '#5AC0B1', fontSize: 11, color: 'white' }}
                onClick={() => {this.addPlantRequest()}}
              >
                <ReduxLanguage languageKey="credits.associateNewPlantToContract" />
              </Button>
            }
          </div>
          <div className={classes.tableContainer}>
            <CustomerContractDomainsTable
              rowsPerPage={pageSize}
              customerDomains={customerContractPlantsPaginated}
              onRowClick={() => {}}
              onPageSizeChange={() => {}}
              onFetchElementsOnPage={() => {}}
              onRemovePlantFromContractRequest={(customerDomain) => this.onRemovePlantFromContractRequest(customerDomain)}
            />
          </div>
        </Card>
      </div>
    );
  }
}


function mapStateToProps(state: IState) {
  return {
    language: state.settings.language,
    userData: state.user.userData,
    selectedCustomerContract: state.credits.customerContracts.selectedCustomerContract,
    customerPlants: state.credits.systemCards.plants.data,
  };
}

const connector = connect(mapStateToProps);

export default connector(withRouter(withStyles(styles)(CustomerContractPlantsSection)));